import { TweenMax, Power2 } from 'gsap/TweenMax';
import { Draggable } from 'gsap/Draggable';

export class ProjectMenuScrollHandler {
	private _container: HTMLElement;
	private _scrollableElement: HTMLElement;
	private _draggable: Draggable;
	private _currentY: number = 0;
	private _targetY: number = 0;
	private _velocity: number = 0;
	private _maxScroll: number = 0;
	private _minScroll: number = 0;
	private _isEnabled: boolean = false;
	private _scrollbarHandle: HTMLElement;
	private _scrollbarTrack: HTMLElement;
	private _arrowUp: HTMLElement;
	private _arrowDown: HTMLElement;
	private _contactMeSection: HTMLElement;

	private _scrollbarEnabled: boolean = false;

	constructor(
		container: HTMLElement, 
		scrollableElement: HTMLElement,
		scrollbarHandle: HTMLElement,
		scrollbarTrack: HTMLElement,
		arrowUp: HTMLElement,
		arrowDown: HTMLElement
	) {
		this._container = container;
		this._scrollableElement = scrollableElement;
		this._scrollbarHandle = scrollbarHandle;
		this._scrollbarTrack = scrollbarTrack;
		this._arrowUp = arrowUp;
		this._arrowDown = arrowDown;
		this._contactMeSection = document.querySelector('.ProjectMenu .contact-me');
		this._setupDraggable();
		this._setupWheelScroll();
	}

	private _setupDraggable(): void {
		this._draggable = Draggable.create(this._scrollableElement, {
			type: 'y',
			bounds: { minY: 0, maxY: 0 },
			trigger: this._container,
			cursor: 'auto',
			inertia: true,
			throwResistance: 3000,
			edgeResistance: 1,
			onDrag: () => {
				this._currentY = this._draggable.y;
				this._targetY = this._currentY;
				this._constrainPosition();
				this._updateScrollbar();
			},
			onThrowUpdate: () => {
				this._currentY = this._draggable.y;
				this._targetY = this._currentY;
				this._constrainPosition();
				this._updateScrollbar();
			},
			onDragEnd: () => {
				this._constrainPosition();
			},
			onThrowComplete: () => {
				this._constrainPosition();
			}
		} as any)[0];

	}

	private _setupWheelScroll(): void {
		const handleWheel = (e: WheelEvent) => {
			if (!this._isEnabled) return;
			
			e.preventDefault();
			
			const delta = e.deltaY;
			this._targetY -= delta;
			this._constrainPosition();
			this._animateToTarget();
		};

		this._container.addEventListener('wheel', handleWheel, { passive: false });
	}

	private _constrainPosition(): void {
		if (this._targetY > this._minScroll) {
			this._targetY = this._minScroll;
		}
		if (this._targetY < this._maxScroll) {
			this._targetY = this._maxScroll;
		}
	}

	private _animateToTarget(): void {
		TweenMax.to(this._scrollableElement, 0.6, {
			y: this._targetY,
			ease: Power2.easeOut,
			overwrite: 'auto',
			onUpdate: () => {
				this._currentY = this._targetY;
				this._draggable.update();
				this._updateScrollbar();
			}
		});
	}

	private _updateScrollbar(): void {
		if (!this._scrollbarHandle || !this._scrollbarTrack) return;

		const scrollRange = this._minScroll - this._maxScroll;
		if (scrollRange <= 0) {
			this.setScrollbarVisibility(false);
			return;
		}

		this.setScrollbarVisibility(true);

		// Calculate handle position (inverted so it moves correctly)
		const scrollProgress = 1 - ((this._currentY - this._maxScroll) / scrollRange);
		const trackHeight = this._scrollbarTrack.offsetHeight;
		const handleHeight = this._scrollbarHandle.offsetHeight;
		const maxHandleY = trackHeight - handleHeight;
		const handleY = scrollProgress * maxHandleY;

		TweenMax.set(this._scrollbarHandle, { y: handleY });

		// Update arrows - arrows point to where MORE content is
		// const threshold = 5;
		// const isAtTop = this._currentY >= (this._minScroll - threshold);
		// const isAtBottom = this._currentY <= (this._maxScroll + threshold);

		// if (this._arrowUp) {
		// 	// Show arrow UP only when scrolled down (more content above)
		// 	if (isAtTop) {
		// 		this._arrowUp.classList.remove('visible');
		// 	} else {
		// 		this._arrowUp.classList.add('visible');
		// 	}
		// }

		// if (this._arrowDown) {
		// 	// Show arrow DOWN only when scrolled up (more content below)
		// 	if (isAtBottom) {
		// 		this._arrowDown.classList.remove('visible');
		// 	} else {
		// 		this._arrowDown.classList.add('visible');
		// 	}
		// }
	}

	private setScrollbarVisibility(visible: boolean): void {
		if(visible === this._scrollbarEnabled) return;

		if (visible) {
			TweenMax.to(this._scrollbarHandle, 0.3, { opacity: 1 });
			TweenMax.to(this._scrollbarTrack, 0.3, { opacity: 0.2 });
		} else {
			TweenMax.to(this._scrollbarHandle, 0.3, { opacity: 0 });
			TweenMax.to(this._scrollbarTrack, 0.3, { opacity: 0 });
		}

		this._scrollbarEnabled = visible;
	}

	public updateBounds(): void {
		const containerHeight = this._container.clientHeight;
		const contactMeHeight = this._contactMeSection ? this._contactMeSection.offsetHeight : 0;
		const availableHeight = containerHeight - contactMeHeight;
		const contentHeight = this._scrollableElement.offsetHeight;
		
		// Calculate scroll bounds
		const overflow = contentHeight - availableHeight;
		
		if (overflow > 0) {
			// Content is larger than container
			// Start at top (y=0), allow scrolling down to -overflow
			this._minScroll = 0;
			this._maxScroll = -overflow;
			
			// Position list at top of container
			TweenMax.set(this._scrollableElement, { y: 0 });
			this._targetY = 0;
			this._currentY = 0;
		} else {
			// Content fits within container - stays centered by flexbox
			this._minScroll = 0;
			this._maxScroll = 0;
			TweenMax.set(this._scrollableElement, { y: 0 });
			this._targetY = 0;
			this._currentY = 0;
		}

		this._draggable.applyBounds({
			minY: this._maxScroll,
			maxY: this._minScroll
		});

		this._draggable.update();
		this._updateScrollbar();
	}

	public enable(): void {
		this._isEnabled = true;
		this._draggable.enable();
		this.updateBounds();
	}

	public disable(): void {
		this._isEnabled = false;
		this._draggable.disable();
		
		// Hide scrollbar when disabled
		if (this._scrollbarHandle && this._scrollbarTrack) {
			this.setScrollbarVisibility(false);
		}
		// if (this._arrowUp && this._arrowDown) {
		// 	this._arrowUp.classList.remove('visible');
		// 	this._arrowDown.classList.remove('visible');
		// }
	}

	public reset(): void {
		this._targetY = 0;
		this._currentY = 0;
		TweenMax.set(this._scrollableElement, { y: 0 });
		this._draggable.update();
		this._updateScrollbar();
	}
}
